/**
 * KennelPublishFlow — modale publikacji Kreatora Hodowli.
 *
 *  phase === 'warn'    → ostrzeżenie o pustej hodowli PRZED publikacją:
 *      „Twoja strona hodowli jest gotowa do publikacji, ale nie dodałaś jeszcze
 *       psów ani miotów..." — CTA: Dodaj sukę · reproduktora · miot · Opublikuj mimo wszystko.
 *
 *  phase === 'success' → ujednolicony Ekran Sukcesu (wspólny komponent PublishSuccessFlow,
 *      entityType='kennel'). Dzięki temu Hodowla, Reproduktor, Miot, Firma wyglądają
 *      IDENTYCZNIE — zmienia się tylko treść.
 *
 * Suki / Reproduktory / Mioty to OSOBNE moduły (Source of Truth) — kreator hodowli
 * tworzy WYŁĄCZNIE stronę hodowli, a tu zachęcamy do uzupełnienia oferty.
 */
import { PawPrint, Award, Dog, Rocket, AlertTriangle, ArrowRight, X, Loader2, Inbox } from 'lucide-react';
import PublishSuccessFlow from '@/components/wizard/PublishSuccessFlow';

const ADD_CTAS = [
  { key: 'dam', icon: PawPrint, label: 'Dodaj sukę', path: '/panel/psy' },
  { key: 'stud', icon: Award, label: 'Dodaj reproduktora', path: '/panel/reproduktory' },
  { key: 'litter', icon: Dog, label: 'Dodaj miot', path: '/panel/mioty' },
];

function Backdrop({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-[3px]"
      onClick={onClose}
      aria-hidden="true"
      data-testid="kennel-publish-backdrop"
    />
  );
}

function Shell({ children }) {
  return (
    <div className="fixed z-[60] inset-0 flex items-center justify-center p-4 pointer-events-none" role="dialog" aria-modal="true">
      <div
        className="relative pointer-events-auto w-full max-w-md rounded-3xl overflow-hidden ring-1 ring-white/8 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.85)]"
        style={{ background: 'linear-gradient(160deg, #04202F 0%, #052B3D 60%, #073447 100%)' }}
      >
        <div
          className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-45"
          style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.55), transparent 65%)' }}
          aria-hidden="true"
        />
        {children}
      </div>
    </div>
  );
}

export default function KennelPublishFlow({
  phase, submitting, slug, userName, hasDogs, entityName, coverImage, subtitle,
  onPublishAnyway, onAddAndPublish, onNavigate, onClose,
}) {
  if (!phase) return null;

  /* ───────────── OSTRZEŻENIE: pusta hodowla ───────────── */
  if (phase === 'warn') {
    return (
      <>
        <Backdrop onClose={submitting ? () => {} : onClose} />
        <Shell>
          <button
            onClick={onClose}
            disabled={submitting}
            data-testid="kennel-warn-dismiss"
            className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center text-white/45 hover:text-white hover:bg-white/8 transition-colors disabled:opacity-40"
            aria-label="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="relative p-7 sm:p-9 space-y-6" data-testid="kennel-publish-warn">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full ring-1 bg-amber-400/15 ring-amber-400/30">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-300" strokeWidth={2.5} />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200">Hodowla bez psów</span>
            </div>
            <h2 className="text-white text-2xl sm:text-[28px] font-bold tracking-tight leading-tight">
              Strona gotowa do publikacji
            </h2>
            <p className="text-white/70 text-sm sm:text-base leading-relaxed">
              Twoja strona hodowli jest gotowa do publikacji, ale nie dodałaś jeszcze psów ani miotów.
              Hodowla bez psów może wyglądać niekompletnie dla odwiedzających.
            </p>

            <div className="space-y-2">
              {ADD_CTAS.map(({ key, icon: Icon, label, path }) => (
                <button
                  key={key}
                  onClick={() => onAddAndPublish(path)}
                  disabled={submitting}
                  data-testid={`kennel-warn-add-${key}`}
                  className="group w-full flex items-center gap-3 p-3.5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.09] ring-1 ring-white/10 hover:ring-[var(--apisai-primary)]/50 transition-all text-left disabled:opacity-50"
                >
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-[var(--apisai-primary)]/15">
                    <Icon className="w-4 h-4 text-[var(--apisai-primary)]" />
                  </span>
                  <span className="flex-1 text-white text-sm font-semibold">{label}</span>
                  <ArrowRight className="w-4 h-4 text-white/45 group-hover:text-[var(--apisai-primary)] flex-shrink-0 transition-colors" />
                </button>
              ))}
            </div>

            <div className="h-px bg-white/8" />

            <button
              onClick={onPublishAnyway}
              disabled={submitting}
              data-testid="kennel-warn-publish-anyway"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm font-semibold transition-all shadow-[0_12px_30px_-8px_rgba(249,115,22,0.6)] hover:-translate-y-0.5 min-h-[48px] disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
              Opublikuj mimo wszystko
            </button>
          </div>
        </Shell>
      </>
    );
  }

  /* ───────────── SUCCESS: wspólny Ekran Sukcesu ───────────── */
  return (
    <PublishSuccessFlow
      entityType="kennel"
      slug={slug}
      userName={userName}
      entityName={entityName}
      coverImage={coverImage}
      subtitle={subtitle}
      onClose={onClose}
      onNavigate={onNavigate}
      nextItems={
        hasDogs
          ? [
              { key: 'litter', icon: Dog, label: 'Dodaj miot', path: '/panel/mioty' },
              { key: 'leads', icon: Inbox, label: 'Przejdź do zapytań', path: '/panel/zapytania' },
            ]
          : ADD_CTAS
      }
    />
  );
}
